import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { consumeMagicLink } from '../services/api';

const isSafeNext = (next: string | null): boolean => {
  if (!next) return false;
  return next.startsWith('/') && !next.startsWith('//') && !next.includes('://');
};

const MagicLink: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  // DECODE-ONCE (paid-flow dependency): URLSearchParams.get already decodes the
  // value exactly once, so nextParam is the FINAL path
  // (e.g. /pricing?tier=pro&checkout=1). Do NOT call decodeURIComponent on it —
  // a second decode corrupts the query string. Validate THIS decoded value with
  // isSafeNext below, then navigate to this same value.
  const nextParam = searchParams.get('next');
  const hasFired = useRef(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleContinue = () => {
    if (hasFired.current) return;
    hasFired.current = true;
    setLoading(true);

    consumeMagicLink(token!)
      .then((response) => {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        const userType = response.data.user.user_type;
        let destination = '/my-quotes';
        if (isSafeNext(nextParam)) {
          destination = nextParam!;
        } else {
          destination = userType === 'customer' ? '/my-requests' : '/my-quotes';
        }
        navigate(destination);
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Invalid or expired link');
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!token) {
      setError('Invalid or expired link');
    }
  }, [token]);

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2>❌ Invalid Link</h2>
          <p style={styles.error}>{error}</p>
          <Link to="/login" style={styles.button}>Go to Login</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2>🔓 Signing you in...</h2>
          <p style={styles.note}>One moment please.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2>🔓 Sign in to QuoteBid</h2>
        <p style={styles.note}>Tap below to securely access your account.</p>
        <button
          onClick={handleContinue}
          disabled={loading}
          style={styles.button}
        >
          Continue →
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', padding: '20px' },
  card: { backgroundColor: 'white', padding: '40px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px', textAlign: 'center' },
  note: { color: '#7f8c8d', marginTop: '20px' },
  error: { color: '#e74c3c', backgroundColor: '#fdeaea', padding: '10px', borderRadius: '5px', marginBottom: '20px' },
  button: { display: 'inline-block', padding: '12px 24px', backgroundColor: '#3498db', color: 'white', borderRadius: '5px', textDecoration: 'none', marginTop: '10px', border: 'none', cursor: 'pointer', fontSize: '16px', fontFamily: 'inherit' },
};

export default MagicLink;
